// useFlip — a tiny dependency-free FLIP (First-Last-Invert-Play) so layout
// changes glide instead of jumping (the "Framer Flow" feel). Whenever `deps`
// change, each tracked child that MOVED in the layout plays its move out with
// the Web Animations API. Honors reduced-motion.
//
// Usage: attach a ref to a container, list the state that reflows it.
//   const ref = useRef(null); useFlip(ref, [summary, chips.length, compact]);
//
// THREE THINGS IT USED TO GET WRONG (2026-10-19, John: "the view chips can
// sometimes fly in and out of view very glitchily"):
//   · it remembered a child's box only when `deps` changed, so "first" could be
//     many renders old. Now every commit records where each child stands, and a
//     change in `deps` compares against the commit just before it.
//   · it measured the VISUAL box (getBoundingClientRect), which carries the
//     transforms riding on top of the layout: the pinned head's fold lifts the
//     chips by a transform, so a fold in flight read as a move, and a slot that
//     was hidden (display: none, a box at 0,0) read as a move from the corner of
//     the screen. Now it measures the LAYOUT offset, and a child that was hidden
//     (or is new) simply stands where it lands.
//   · its animation REPLACED the element's transform for its whole run, so the
//     fold's lift was dropped and the chips snapped back once it ended. Now the
//     move is ADDED to whatever transform the element already wears
//     (composite: "add"), so the two compose.

import { useLayoutEffect, useRef } from "react";

type Spot = { x: number; y: number };

/** where a child stands in the layout, transforms aside; null when it has no box */
function spotOf(el: Element): Spot | null {
  const h = el as HTMLElement;
  if (!h.offsetParent && getComputedStyle(h).position !== "fixed") return null;
  if (h.offsetWidth === 0 && h.offsetHeight === 0) return null;
  return { x: h.offsetLeft, y: h.offsetTop };
}

export function useFlip(
  ref: React.RefObject<HTMLElement | null>,
  deps: React.DependencyList,
  opts?: { duration?: number; easing?: string; selector?: string; /** children matching this never glide (they bring their own motion) */ skip?: string },
) {
  const prev = useRef<Map<Element, Spot>>(new Map());
  const changed = useRef(false);

  // the deps changed in this commit: play against what the last commit recorded
  useLayoutEffect(() => {
    changed.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // every commit: record where each child stands (and play, when asked)
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const kids = opts?.selector ? Array.from(el.querySelectorAll(opts.selector)) : Array.from(el.children);
    const next = new Map<Element, Spot>();
    for (const k of kids) {
      const s = spotOf(k);
      if (s) next.set(k, s);
    }
    const play = changed.current;
    changed.current = false;
    const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (play && !reduced) {
      for (const [k, r] of next) {
        if (opts?.skip && k.matches(opts.skip)) continue;
        const p = prev.current.get(k);
        if (!p) continue;
        const dx = p.x - r.x;
        const dy = p.y - r.y;
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
        try {
          (k as HTMLElement).animate(
            [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "translate(0px, 0px)" }],
            { duration: opts?.duration ?? 340, easing: opts?.easing ?? "cubic-bezier(0.18, 0.72, 0.22, 1)", composite: "add" },
          );
        } catch {
          /* no additive animation here: it lands without the glide */
        }
      }
    }
    prev.current = next;
  });
}
